// pages/topics/aimodels/cnn.js
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// import { Legend } from 'recharts'; // Currently unused
import topicDetails from "../topicData";

// WebSocket URL (same as in your AITraining component)
const WEBSOCKET_URL = "wss://7fuet5fuyl.execute-api.us-east-1.amazonaws.com/prod";

const Cnn = () => {
  const { state } = useLocation();
  const { modelData: initialModelData } = state || {};
  const navigate = useNavigate(); 
  const topicKey = "cnn"; // Match the topicComponents key
  const topic = topicDetails[topicKey] || { title: "CNN Model Details", content: "" };
  
  // State management
  const [modelData, setModelData] = useState(initialModelData);
  const [trainingMetrics, setTrainingMetrics] = useState([]);
  const [activeMetrics, setActiveMetrics] = useState(["loss", "mae"]);
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
// New state for prediction chart toggles - default to showing both
const [activePredictionMetrics, setActivePredictionMetrics] = useState(["prediction", "true_value"]);
  // Reference for WebSocket connection
  const socketRef = useRef(null);
  // Toggle prediction metrics visibility
const togglePredictionMetric = (metricId) => {
    setActivePredictionMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      } else {
        return [...prev, metricId];
      }
    });
  };

 // Available metrics to display
const availableMetrics = [
    { id: "loss", name: "Loss", color: "#ff4d4f" },
    { id: "mae", name: "MAE", color: "#1890ff" },
    { id: "prediction", name: "Prediction", color: "#FA8C16" },
    { id: "true_value", name: "True Value", color: "#52C41A" }
  ];
  
  // Load historical data from localStorage on component mount
  useEffect(() => {
    try {
      // Load training messages from localStorage
      const storedMessages = localStorage.getItem('ai-training-messages');
      if (storedMessages) {
        const messages = JSON.parse(storedMessages);
        const Messages = messages.filter(msg => msg.model_name === "CNN");
        
        // Sort messages by epoch for proper visualization
        const sortedMessages = Messages.sort((a, b) => {
          const epochA = a.epoch || a.payload?.epoch || 0;
          const epochB = b.epoch || b.payload?.epoch || 0;
          return epochA - epochB;
        });
        
        // Transform messages into chart-friendly format
        const chartData = sortedMessages.map(msg => {
            const epoch = msg.epoch || msg.payload?.epoch || 0;
            const loss = msg.loss || msg.metrics?.loss || msg.payload?.loss || 0;
            const mae = msg.mae || msg.metrics?.mae || msg.payload?.mae || 0;
            // Extract prediction and true value data if available
            const prediction = msg.prediction || msg.samples?.[0]?.prediction || null;
            const true_value = msg.true_value || msg.samples?.[0]?.true_value || null;
          
       return {
  epoch,
  loss,
  mae,
  timestamp: msg.timestamp || Date.now(),
  ...(prediction !== null ? { prediction } : {}),
  ...(true_value !== null ? { true_value } : {})
          };
        });
        
        setTrainingMetrics(chartData);
      }
    } catch (error) {
      console.error("Failed to load historical training data:", error);
    }
  }, []);
  
  // Set up WebSocket connection for real-time updates
  useEffect(() => {
    // Create WebSocket connection
    socketRef.current = new WebSocket(WEBSOCKET_URL);
    const socket = socketRef.current;
    
    socket.onopen = () => {
      console.log("WebSocket connection established for CNN model page");
      setConnectionStatus("Connected");
    };
    
    socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Process only CNN model updates
          if (data.model_name === "CNN") {
            // Update model data state for current view
            setModelData(prevData => {
              if (!prevData) return data; // If no previous data, use the new data directly
              
              const newData = {
                epoch: data.epoch || data.payload?.epoch || (prevData?.epoch || 0),
                metrics: {
                  loss: data.loss || data.metrics?.loss || data.payload?.loss || (prevData?.metrics?.loss || 0),
                  mae: data.mae || data.metrics?.mae || data.payload?.mae || (prevData?.metrics?.mae || 0)
                },
                timestamp: data.timestamp || Date.now(),
                system_stats: data.system_stats || prevData?.system_stats || null,
                samples: data.samples || prevData?.samples || []
              };
              return newData;
            });
            
            // Add to metrics chart data for historical tracking
            setTrainingMetrics(prevMetrics => {
              const epoch = data.epoch || data.payload?.epoch || 0;
              const loss = data.loss || data.metrics?.loss || data.payload?.loss || 0;
              const mae = data.mae || data.metrics?.mae || data.payload?.mae || 0;
              const timestamp = data.timestamp || Date.now();
              
              // Calculate average prediction and true value from samples if available
              let avgPrediction = null;
              let avgTrueValue = null;
              
              if (data.samples && data.samples.length > 0) {
                const totalPrediction = data.samples.reduce((sum, sample) => sum + sample.prediction, 0);
                const totalTrueValue = data.samples.reduce((sum, sample) => sum + sample.true_value, 0);
                
                avgPrediction = totalPrediction / data.samples.length;
                avgTrueValue = totalTrueValue / data.samples.length;
              }
              
              // Check if this epoch already exists
              const existingIndex = prevMetrics.findIndex(m => m.epoch === epoch);
              
              if (existingIndex >= 0) {
                // Update existing epoch data
                const updatedMetrics = [...prevMetrics];
                updatedMetrics[existingIndex] = { 
                  epoch, 
                  loss, 
                  mae, 
                  timestamp,
                  // Only add prediction and true value if we have samples
                  ...(avgPrediction !== null ? { prediction: avgPrediction } : {}),
                  ...(avgTrueValue !== null ? { true_value: avgTrueValue } : {})
                };
                return updatedMetrics;
              } else {
                // Add new epoch data
                const newMetric = { 
                  epoch, 
                  loss, 
                  mae, 
                  timestamp,
                  // Only add prediction and true value if we have samples
                  ...(avgPrediction !== null ? { prediction: avgPrediction } : {}),
                  ...(avgTrueValue !== null ? { true_value: avgTrueValue } : {})
                };
                
                return [...prevMetrics, newMetric].sort((a, b) => a.epoch - b.epoch);
              }
            });
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error);
        }
      };
    
    socket.onerror = (error) => {
      console.error("WebSocket error onCNN model page:", error);
      setConnectionStatus("Error");
    };
    
    socket.onclose = () => {
      console.log("WebSocket connection closed for CNN model page");
      setConnectionStatus("Disconnected");
    };
    
    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);
  
  // Toggle metrics visibility
  const toggleMetric = (metricId) => {
    setActiveMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      } else {
        return [...prev, metricId];
      }
    });
  };
  
  // Get metric color by ID
  const getMetricColor = (metricId) => {
    return availableMetrics.find(m => m.id === metricId)?.color || "#000";
  };
  
  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 p-3 rounded shadow-lg text-white text-sm">
          <p className="font-medium mb-1">Epoch: {label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.stroke }}>
              {entry.dataKey === "loss" ? "Loss: " : 
               entry.dataKey === "mae" ? "MAE: " :
               entry.dataKey === "prediction" ? "Prediction: " :
               entry.dataKey === "true_value" ? "True Value: " : ""}
              {entry.value.toFixed(4)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!modelData) {
    return (
      <div className="w-full h-full overflow-auto">
        <div className="container mx-auto px-4 py-8" style={{ paddingTop: "calc(var(--navbar-height) + 1.5rem)" }}>
          <h1 className="text-2xl font-bold mb-4">CNN Model Details</h1>
          <p>No CNN model data available. Please return to the main page and try again.</p>
          <button
            onClick={() => navigate("/topics/ai-training")}
            className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Back to Training Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{topic.title} - limkc</title>
        <meta name="description" content={topic.content} />
      </Helmet>

      <div className="w-full h-full overflow-auto">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-6" style={{ paddingTop: "calc(var(--navbar-height) + 1.5rem)" }}>

        <div className="flex items-center mb-6">
          <h1 className="text-3xl font-bold">{topic.title}</h1>
          <div className="ml-auto flex items-center space-x-4">
            <div className={`w-3 h-3 rounded-full ${
              connectionStatus === 'Connected' ? 'bg-green-600 animate-pulse' :
              connectionStatus === 'Error' ? 'bg-red-600' :
              'bg-gray-400'
            }`}></div>
            <div className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
              Epoch: {modelData.epoch}
            </div>
          </div>
        </div>

        <p className="text-gray-700 mt-2 mb-6">{topic.content}</p>
        
         {/* Main Chart Section */}
         <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex flex-wrap items-center justify-between mb-4">
            <h2 className="text-l font-bold">Training Metrics</h2>
            <div className="flex space-x-1 mt-2 sm:mt-0">
            {availableMetrics.slice(0,2).map(metric => (
        <button
          key={metric.id}
          onClick={() => toggleMetric(metric.id)}
          className={`px-1 py-0.5 rounded text-[10px] font-normal flex items-center ${
            activeMetrics.includes(metric.id) 
              ? 'bg-gray-700 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <span 
            className="w-1.5 h-1.5 rounded-full mr-1" 
                    style={{ backgroundColor: metric.color }}
                  ></span>
                  {metric.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="h-80">
            {trainingMetrics.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={trainingMetrics}
                  margin={{ top: 10, right: 30, left: 10, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis 
                    dataKey="epoch"
                    type="number"
                    label={{ value: 'Epoch', position: 'bottom', offset: 0 }}
                    domain={['dataMin', 'dataMax']}
                    tickCount={10}
                  />
                  <YAxis 
                    domain={['auto', 'auto']}
                    tickCount={7}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {/* <Legend 
    verticalAlign="bottom" 
    align="center"
    wrapperStyle={{ 
      paddingTop: 20,
      bottom: 0,
      lineHeight: '24px'
    }}
    iconSize={10}
    iconType="circle"
  /> */}
                  
                  {activeMetrics.includes("loss") && (
                    <Line
                      type="monotone"
                      dataKey="loss"
                      stroke={getMetricColor("loss")}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                    />
                  )}
                  
                  {activeMetrics.includes("mae") && (
                    <Line
                      type="monotone"
                      dataKey="mae"
                      stroke={getMetricColor("mae")}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">No training data available</p>
              </div>
            )}
          </div>
        </div>


        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
  <div className="flex flex-wrap items-center justify-between mb-4">
  <h2 className="text-l font-bold">Estimates | True Values</h2>
    <div className="flex space-x-1 mt-2 sm:mt-0">
      {availableMetrics.slice(2).map(metric => (
        <button
  key={metric.id}
  onClick={() => togglePredictionMetric(metric.id)}
  className={`px-1 py-0.5 rounded text-[10px] font-normal flex items-center ${
    activePredictionMetrics.includes(metric.id) 
      ? 'bg-gray-700 text-white' 
      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
  }`}
>
  <span 
    className="w-1.5 h-1.5 rounded-full mr-1" 
    style={{ backgroundColor: metric.color }}
  ></span>
  {metric.name}
</button>
      ))}
    </div>
  </div>
  
  <div className="h-80">
    {trainingMetrics.some(m => m.prediction !== undefined && m.true_value !== undefined) ? (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={trainingMetrics}
          margin={{ top: 10, right: 30, left: 10, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis 
            dataKey="epoch"
            type="number"
            label={{ value: 'Epoch', position: 'bottom', offset: 0 }}
            domain={['dataMin', 'dataMax']}
          />
          <YAxis 
            name="Value"
            domain={['auto', 'auto']}
            label={{ value: 'Value', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip 
            formatter={(value, name) => [value.toFixed(2), name === 'true_value' ? 'True Value' : 'Prediction']}
            labelFormatter={(value) => `Epoch ${value}`}
          />
          {/* <Legend 
            verticalAlign="bottom" 
            align="center"
            wrapperStyle={{ 
              paddingTop: 20,
              bottom: 0,
              lineHeight: '24px'
            }}
            iconSize={10}
            iconType="circle"
          /> */}
          
          {/* Line for average true values across epochs */}
         {activePredictionMetrics.includes("prediction") && (
  <Line
    type="monotone"
    dataKey="prediction"
    stroke={getMetricColor("prediction")}
    strokeWidth={2}
    dot={false}
    activeDot={{ r: 6 }}
    isAnimationActive={false}
  />
)}

{activePredictionMetrics.includes("true_value") && (
  <Line
    type="monotone"
    dataKey="true_value"
    stroke={getMetricColor("true_value")}
    strokeWidth={2}
    dot={false}
    activeDot={{ r: 6 }}
    isAnimationActive={false}
  />
)}
        </LineChart>
      </ResponsiveContainer>
    ) : (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-500">No prediction/true value data available</p>
      </div>
    )}
  </div>
</div>

        {/* Last Update - Moved outside the card */}
<div className="text-sm text-gray-500 mt-2 mb-6">
  Last Update: {new Date(modelData.timestamp).toLocaleString()}
</div>
        
        {/* System Stats & Current Metrics Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Current Training Metrics */}
     
<div className="bg-white rounded-lg shadow-md p-4">
  <h2 className="text-lg font-semibold mb-3">Current Metrics</h2>
  
  <div className="space-y-4">
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium">Loss</span>
        <span className="text-sm font-medium">{typeof modelData.metrics.loss === 'number' ? modelData.metrics.loss.toFixed(6) : modelData.metrics.loss}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className="bg-red-500 h-2.5 rounded-full" 
          style={{ width: `${Math.min(modelData.metrics.loss * 10, 100)}%` }}
        ></div>
      </div>
    </div>
    
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium">MAE</span>
        <span className="text-sm font-medium">{typeof modelData.metrics.mae === 'number' ? modelData.metrics.mae.toFixed(4) : modelData.metrics.mae}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className="bg-blue-500 h-2.5 rounded-full" 
          style={{ width: `${Math.min(modelData.metrics.mae, 100)}%` }}
        ></div>
      </div>
    </div>
  </div>
</div>



{/* CPU Stats */}
{modelData.system_stats && (
  <div className="bg-white rounded-lg shadow-md p-4">
    <h2 className="text-lg font-semibold mb-3">CPU Resources</h2>
    
    <div className="space-y-4">
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium">CPU Usage</span>
          <span className="text-sm font-medium">{modelData.system_stats.cpu['CPU Usage (%)']}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${
              modelData.system_stats.cpu['CPU Usage (%)'] > 90 ? 'bg-red-500' : 
              modelData.system_stats.cpu['CPU Usage (%)'] > 70 ? 'bg-yellow-500' : 
              'bg-green-500'
            }`}
            style={{ width: `${modelData.system_stats.cpu['CPU Usage (%)']}%` }}
          ></div>
        </div>
      </div>
      
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium">MEM Usage</span>
          <span className="text-sm font-medium">
            {(modelData.system_stats.cpu['Memory Used (MB)'] / 1024).toFixed(1)} / 
            {(modelData.system_stats.cpu['Memory Total (MB)'] / 1024).toFixed(1)} GB
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${
              (modelData.system_stats.cpu['Memory Used (MB)'] / modelData.system_stats.cpu['Memory Total (MB)']) > 0.9 ? 'bg-red-500' : 
              (modelData.system_stats.cpu['Memory Used (MB)'] / modelData.system_stats.cpu['Memory Total (MB)']) > 0.7 ? 'bg-yellow-500' : 
              'bg-blue-500'
            }`}
            style={{ width: `${(modelData.system_stats.cpu['Memory Used (MB)'] / modelData.system_stats.cpu['Memory Total (MB)']) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  </div>
)}

{/* GPU Stats with Temperature on same line */}
{modelData.system_stats && modelData.system_stats.gpu && modelData.system_stats.gpu.length > 0 && (
    <div className="bg-white rounded-lg shadow-md p-4">
  <div className="flex justify-between items-center mb-3">
    <h2 className="text-lg font-semibold">GPU Resources</h2>
    <span
  className={`px-1 py-1 rounded text-xs font-medium ${
    modelData.system_stats.gpu[0]['Temperature (°C)'] > 80 ? 'bg-red-100 text-red-800' : 
    modelData.system_stats.gpu[0]['Temperature (°C)'] > 70 ? 'bg-yellow-100 text-yellow-800' : 
    'bg-green-100 text-green-800'
  }`}
>
      {modelData.system_stats.gpu[0]['Temperature (°C)']}°C
    </span>
  </div>
  
  <div className="space-y-4">
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium">GPU Usage</span>
        <span className="text-sm font-medium">{modelData.system_stats.gpu[0]['Usage (%)']}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className="bg-purple-500 h-2.5 rounded-full" 
          style={{ width: `${modelData.system_stats.gpu[0]['Usage (%)']}%` }}
        ></div>
      </div>
    </div>
    
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium">GPU Memory</span>
        <span className="text-sm font-medium">
          {(modelData.system_stats.gpu[0]['Memory Used (MB)'] / 1024).toFixed(1)} / 
          {(modelData.system_stats.gpu[0]['Memory Total (MB)'] / 1024).toFixed(1)} GB
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className="bg-indigo-500 h-2.5 rounded-full" 
          style={{ width: `${(modelData.system_stats.gpu[0]['Memory Used (MB)'] / modelData.system_stats.gpu[0]['Memory Total (MB)']) * 100}%` }}
        ></div>
      </div>
    </div>
  </div>
</div>
)}
        </div>
        
        {/* Recent Epochs Table */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">Recent Training Steps</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Epoch
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loss
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MAE
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trainingMetrics.slice(-10).reverse().map((metric) => (
                  <tr key={metric.epoch} className="hover:bg-gray-50">
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {metric.epoch}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      {metric.loss.toFixed(4)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      {metric.mae.toFixed(4)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      {new Date(metric.timestamp || Date.now()).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Button at the bottom */}
        <div className="mt-6 flex justify-start mb-8">
          <button
            onClick={() => navigate("/topics/ai-training")}
            className="text-sm px-6 py-1 rounded bg-gray-900 hover:bg-gray-700 text-white transition-colors duration-300"
          >
           &lt; Back
          </button>
        </div>
        </div>
      </div>
    </>
  );
};

export default Cnn;